import React, { useEffect, useCallback } from 'react';
import iconDepositPoints from 'images/icon_deposit_points.svg';
import iconWithdrawPoints from 'images/icon_withdraw_points.svg';
import styles from 'features/points/index.module.scss';
import {
  SELECT_NEW_PAGE,
  SELECTED_TAB,
  STATE_DEPOSIT,
  STATE_WITHDRAW,
  usePointsContext,
} from 'features/points/use-points';
import LoadingCard from 'componets/loading-card';
import numeral from 'numeral';
import moment from 'moment';
import get from 'lodash/get';
import classname from 'classnames/bind';
import { CHANGE_CURRENT_PAGE, PAGE_POINTS, useAppContext } from 'use-app';
import uuid from 'uuid';
import qs from 'query-string';
import { useHistory } from 'react-router-dom';
import Pagination from 'componets/pagination';
import { ChevronRightIcon } from 'icons';
import analytics from 'service/analytics';

const cx = classname.bind(styles);

const MochiCoinTransaction = ({ mochiCoinTransaction, state }) => {
  const { title, balance, startAt, endAt, url } = mochiCoinTransaction;
  const { orderReadable } = usePointsContext();

  const history = useHistory();

  const RedirectButton = () => {
    if (orderReadable && url !== null) {
      return <ChevronRightIcon color="#DDDDDD" width={16} height={16} />;
    } else {
      return <></>;
    }
  };

  const itemClasses = cx({
    transaction: true,
    clickable: orderReadable && url !== null,
  });

  const onOrderClick = () => {
    if (orderReadable && url !== null) {
      history.push({
        pathname: '/orders/detail',
        search: qs.stringify({ url: url }),
      });
    }
  };

  return (
    <>
      <div className={itemClasses} onClick={onOrderClick}>
        <div className={styles.transactionContainer}>
          <div className={styles.transactionLeft}>
            <div className={styles.transactionTitle}>{title}</div>
            <div className={styles.transactionExpiredDate}>
              {moment.parseZone(startAt).format('YYYY.MM.DD')}（{moment.parseZone(endAt).format('YYYY.MM.DD')} 到期）
            </div>
          </div>
          <div className={styles.transactionRight}>
            <img src={state === STATE_DEPOSIT ? iconDepositPoints : iconWithdrawPoints} alt="points" />
            {numeral(balance).format('0,0')}
          </div>
        </div>
        <div className={styles.arrow}>
          <RedirectButton />
        </div>
      </div>
      <div className={styles.divide} />
    </>
  );
};

const MochiCoinCard = () => {
  const { state } = useAppContext();

  const ExpiredMochiCoin = () => {
    const expiringMochiCoin = get(state, 'me.mochiCoin.expiringMochiCoin', null);
    if (expiringMochiCoin) {
      return <div className={styles.expired}>有 {expiringMochiCoin.balance} 點將於三十天內到期</div>;
    } else {
      return <></>;
    }
  };

  return (
    <div className={styles.pointsCard}>
      <div className={styles.points}>
        <img src={iconDepositPoints} alt="points" />
        {get(state, 'me.mochiCoin.balance', 0)}
      </div>
      <ExpiredMochiCoin />
    </div>
  );
};

const MochiCoinTabs = () => {
  const { state, dispatch } = usePointsContext();

  const selectedTab = useCallback(
    state => {
      dispatch({
        type: SELECTED_TAB,
        payload: state,
      });
    },
    [dispatch]
  );

  const depositTabClasses = cx({
    tab: true,
    active: state.state === STATE_DEPOSIT,
  });

  const withdrawTabClasses = cx({
    tab: true,
    active: state.state === STATE_WITHDRAW,
  });

  return (
    <div className={styles.tabs}>
      <div
        className={depositTabClasses}
        onClick={() => {
          analytics.track('紅利頁_已獲得_click');
          selectedTab(STATE_DEPOSIT);
        }}
      >
        已獲得
      </div>
      <div
        className={withdrawTabClasses}
        onClick={() => {
          analytics.track('紅利頁_已使用或過期_click');
          selectedTab(STATE_WITHDRAW);
        }}
      >
        已使用或過期
      </div>
    </div>
  );
};

const MochiCoinTransactions = () => {
  const { state, dispatch } = usePointsContext();

  const selectNewPage = useCallback(
    newPage => {
      dispatch({
        type: SELECT_NEW_PAGE,
        payload: newPage,
      });
    },
    [dispatch]
  );

  const NoMochiCoinTransaction = () => {
    return (
      <div className={styles.empty}>
        {state.state === STATE_DEPOSIT ? '沒有可使用企業紅利' : '沒有已使用或過期企業紅利'}
      </div>
    );
  };

  const PermissionDenied = () => {
    return <div className={styles.empty}>權限不足無法瀏覽</div>;
  };

  if (state.pending) {
    return <LoadingCard />;
  } else if (state.permissionDenied) {
    return <PermissionDenied />;
  } else if (state.transactions.length === 0) {
    return <NoMochiCoinTransaction />;
  } else {
    return (
      <div className={styles.transactions}>
        {state.transactions.map(transaction => {
          return <MochiCoinTransaction key={uuid()} mochiCoinTransaction={transaction} state={state.state} />;
        })}
        <div className={styles.pagination}>
          <Pagination currentPage={state.page} totalPage={state.totalPage} onPageSelected={selectNewPage} />
        </div>
      </div>
    );
  }
};

const Points = () => {
  const { state, fetchMochiCoinTransactions } = usePointsContext();

  useEffect(() => {
    if (state.pending) {
      fetchMochiCoinTransactions(state.state, state.page);
    }
  }, [state.pending, state.state, state.page]); // eslint-disable-line

  return (
    <div className={styles.main}>
      <div className={styles.title}>企業紅利</div>
      <div className={styles.desc}>您的企業紅利點數</div>
      <MochiCoinCard />
      <MochiCoinTabs />
      <div className={`${styles.content} ${styles.record}`}>
        <MochiCoinTransactions />
      </div>
    </div>
  );
};

const PointsPage = () => {
  const { dispatch, fetchUserInfo } = useAppContext();

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.track('紅利頁_pageview');
  }, []);

  useEffect(() => {
    dispatch({
      type: CHANGE_CURRENT_PAGE,
      payload: PAGE_POINTS,
    });
    fetchUserInfo();
  }, [dispatch]); //eslint-disable-line

  return (
    <usePointsContext.Provider>
      <Points />
    </usePointsContext.Provider>
  );
};

export default PointsPage;
